import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PlugsConnected, Detective, ListChecks, Copy, ArrowsClockwise, Plus, Trash, PencilSimple, DownloadSimple, UploadSimple, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ["Malware", "Phishing", "Unauthorized Access", "Account Compromise", "Data Exfiltration", "DoS/DDoS", "Insider Threat", "Vulnerability/Exploit", "Policy Violation", "Other"];

export default function SocPhase3Settings() {
  return (
    <>
      <WebhookIngestCard />
      <ThreatIntelCard />
      <TriageRulesCard />
      <SlaEscalationCard />
      <PlaybooksCard />
      <TaskTemplatesCard />
    </>
  );
}

const OPERATORS = ["contains", "equals", "not_equals", "not_contains", "in", "regex"];
const RULE_FIELDS = ["title", "description", "source", "category", "reporter", "client", "severity"];
const SEVERITIES = ["Critical", "High", "Medium", "Low"];

function TriageRulesCard() {
  const [rules, setRules] = useState([]);
  const [teams, setTeams] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/soc/triage-rules").then(r => setRules(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/soc/teams").then(r => setTeams(r.data)).catch(() => {}); }, []);

  const blank = () => ({ name: "", priority: 100, enabled: true, conditions: [{ field: "title", operator: "contains", value: "" }], actions: { set_severity: "", add_tags: [], set_team_id: "" } });
  const save = async () => {
    if (!edit.name.trim()) { toast.error("Rule name required"); return; }
    const body = { ...edit, actions: { ...edit.actions, add_tags: (edit.actions.tagsText || "").split(",").map(s => s.trim()).filter(Boolean) } };
    delete body.actions.tagsText;
    if (!body.actions.set_severity) delete body.actions.set_severity;
    if (!body.actions.set_team_id) delete body.actions.set_team_id;
    try {
      if (edit.id) await api.patch(`/soc/triage-rules/${edit.id}`, body);
      else await api.post("/soc/triage-rules", body);
      setEdit(null); load(); toast.success("Rule saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => { try { await api.delete(`/soc/triage-rules/${id}`); load(); } catch { toast.error("Delete failed"); } };
  const openEdit = (r) => setEdit(r ? { ...r, actions: { ...r.actions, tagsText: (r.actions?.add_tags || []).join(", ") } } : blank());

  return (
    <Card className="p-5 border-border rounded-sm space-y-3" data-testid="soc-triage-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2"><ListChecks size={14} /> Triage Rules</div>
          <p className="text-xs text-muted-foreground mt-1">Applied at case creation in priority order: match conditions → set severity, tags or team.</p>
        </div>
        <Button data-testid="soc-triage-new" size="sm" variant="outline" className="rounded-sm text-xs font-mono uppercase" onClick={() => openEdit(null)}><Plus size={14} className="mr-1" /> New rule</Button>
      </div>
      <div className="space-y-1.5">
        {rules.map(r => (
          <div key={r.id} data-testid={`soc-triage-${r.id}`} className="flex items-center gap-2 text-sm border border-border/60 rounded-sm px-2 py-1.5">
            <span className="font-mono text-[10px] text-muted-foreground w-8">#{r.priority}</span>
            <span className="flex-1 truncate">{r.name}</span>
            <span className={`text-[9px] font-mono px-1.5 py-0.5 border rounded-sm ${r.enabled ? "border-rag-green/40 text-rag-green-fg" : "border-border text-muted-foreground"}`}>{r.enabled ? "on" : "off"}</span>
            <button data-testid={`soc-triage-edit-${r.id}`} onClick={() => openEdit(r)} className="text-muted-foreground hover:text-primary"><PencilSimple size={14} /></button>
            <button data-testid={`soc-triage-del-${r.id}`} onClick={() => del(r.id)} className="text-muted-foreground hover:text-rag-red"><Trash size={13} /></button>
          </div>
        ))}
        {rules.length === 0 && <div className="text-xs text-muted-foreground">No triage rules.</div>}
      </div>

      <Dialog open={!!edit} onOpenChange={o => !o && setEdit(null)}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="soc-triage-dialog">
          <DialogHeader><DialogTitle className="font-mono uppercase text-sm">{edit?.id ? "Edit" : "New"} triage rule</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3 text-sm">
              <div className="flex gap-2">
                <Input data-testid="soc-triage-name" value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} placeholder="Rule name" className="rounded-sm" />
                <Input data-testid="soc-triage-priority" type="number" value={edit.priority} onChange={e => setEdit({ ...edit, priority: parseInt(e.target.value || "100", 10) })} className="rounded-sm w-24" title="Priority (lower first)" />
                <label className="flex items-center gap-1 text-xs"><Switch data-testid="soc-triage-enabled" checked={edit.enabled} onCheckedChange={v => setEdit({ ...edit, enabled: v })} /> on</label>
              </div>
              <div className="text-[10px] font-mono uppercase text-muted-foreground">Conditions (all must match)</div>
              {edit.conditions.map((c, i) => (
                <div key={i} className="flex gap-1.5">
                  <Select value={c.field} onValueChange={v => setEdit({ ...edit, conditions: edit.conditions.map((x, j) => j === i ? { ...x, field: v } : x) })}>
                    <SelectTrigger className="rounded-sm h-8 text-xs w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>{RULE_FIELDS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={c.operator} onValueChange={v => setEdit({ ...edit, conditions: edit.conditions.map((x, j) => j === i ? { ...x, operator: v } : x) })}>
                    <SelectTrigger className="rounded-sm h-8 text-xs w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>{OPERATORS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input value={c.value} onChange={e => setEdit({ ...edit, conditions: edit.conditions.map((x, j) => j === i ? { ...x, value: e.target.value } : x) })} placeholder="value" className="rounded-sm h-8 text-xs flex-1" />
                  <button onClick={() => setEdit({ ...edit, conditions: edit.conditions.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-rag-red"><Trash size={13} /></button>
                </div>
              ))}
              <Button size="sm" variant="ghost" className="rounded-sm text-xs h-7" onClick={() => setEdit({ ...edit, conditions: [...edit.conditions, { field: "title", operator: "contains", value: "" }] })}>+ condition</Button>
              <div className="text-[10px] font-mono uppercase text-muted-foreground">Actions</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Select value={edit.actions.set_severity || "none"} onValueChange={v => setEdit({ ...edit, actions: { ...edit.actions, set_severity: v === "none" ? "" : v } })}>
                  <SelectTrigger data-testid="soc-triage-severity" className="rounded-sm h-8 text-xs"><SelectValue placeholder="Set severity" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— severity —</SelectItem>{SEVERITIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={edit.actions.set_team_id || "none"} onValueChange={v => setEdit({ ...edit, actions: { ...edit.actions, set_team_id: v === "none" ? "" : v } })}>
                  <SelectTrigger className="rounded-sm h-8 text-xs"><SelectValue placeholder="Set team" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— team —</SelectItem>{teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid="soc-triage-tags" value={edit.actions.tagsText || ""} onChange={e => setEdit({ ...edit, actions: { ...edit.actions, tagsText: e.target.value } })} placeholder="tags, comma-sep" className="rounded-sm h-8 text-xs" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm text-xs font-mono uppercase" onClick={() => setEdit(null)}>Cancel</Button>
            <Button data-testid="soc-triage-save" className="rounded-sm text-xs font-mono uppercase" onClick={save}>Save rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const ESC_KINDS = [
  { v: "assignee", label: "Assigned analyst" },
  { v: "team_lead", label: "Team Lead" },
  { v: "admin", label: "Admin" },
  { v: "ciso", label: "CISO" },
];
function SlaEscalationCard() {
  const [ov, setOv] = useState({});
  const [esc, setEsc] = useState(null);
  const [cat, setCat] = useState("Phishing");
  const [resp, setResp] = useState("");
  const [resl, setResl] = useState("");
  const [cases, setCases] = useState([]);
  const [sampleId, setSampleId] = useState("");
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    api.get("/soc/sla/overrides").then(r => setOv(r.data)).catch(() => {});
    api.get("/soc/escalation/config").then(r => setEsc(r.data)).catch(() => {});
    api.get("/soc/cases").then(r => setCases((r.data || []).slice(0, 40))).catch(() => {});
  }, []);
  // Live preview: resolve each step to real recipients for the chosen sample case (or unassigned/no-team).
  useEffect(() => {
    if (!esc) return;
    const t = setTimeout(() => {
      api.post("/soc/escalation/preview", { steps: esc.steps || [], ciso_email: esc.ciso_email || "", case_id: sampleId || undefined })
        .then(r => setPreview(r.data)).catch(() => setPreview(null));
    }, 250);
    return () => clearTimeout(t);
  }, [esc, sampleId]);
  const addOverride = async () => {
    if (!resp || !resl) { toast.error("Enter response + resolution minutes"); return; }
    const next = { ...ov, [cat]: { response: parseInt(resp, 10), resolution: parseInt(resl, 10) } };
    try { const { data } = await api.put("/soc/sla/overrides", next); setOv(data); setResp(""); setResl(""); toast.success("Override saved"); }
    catch (e) { toast.error("Save failed"); }
  };
  const delOverride = async (c) => { const next = { ...ov }; delete next[c]; const { data } = await api.put("/soc/sla/overrides", next); setOv(data); };
  const saveEsc = async () => { try { const { data } = await api.put("/soc/escalation/config", esc); setEsc(data); toast.success("Escalation saved"); } catch { toast.error("Save failed"); } };

  return (
    <Card className="p-5 border-border rounded-sm space-y-4" data-testid="soc-sla-escalation-card">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">SLA Category Overrides</div>
        <p className="text-xs text-muted-foreground mt-1">Per-category SLA (minutes) takes precedence over the severity default.</p>
      </div>
      <div className="space-y-1.5">
        {Object.entries(ov).map(([c, t]) => (
          <div key={c} data-testid={`soc-sla-ov-${c}`} className="flex items-center gap-2 text-sm border border-border/60 rounded-sm px-2 py-1.5">
            <span className="flex-1">{c}</span>
            <span className="font-mono text-xs text-muted-foreground">resp {t.response}m · resl {t.resolution}m</span>
            <button onClick={() => delOverride(c)} className="text-muted-foreground hover:text-rag-red"><Trash size={13} /></button>
          </div>
        ))}
        {Object.keys(ov).length === 0 && <div className="text-xs text-muted-foreground">No overrides — all categories use severity defaults.</div>}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <Select value={cat} onValueChange={setCat}><SelectTrigger data-testid="soc-sla-ov-cat" className="rounded-sm h-8 text-xs w-40"><SelectValue /></SelectTrigger>
          <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        <Input data-testid="soc-sla-ov-resp" type="number" value={resp} onChange={e => setResp(e.target.value)} placeholder="response min" className="rounded-sm h-8 text-xs w-28" />
        <Input data-testid="soc-sla-ov-resl" type="number" value={resl} onChange={e => setResl(e.target.value)} placeholder="resolution min" className="rounded-sm h-8 text-xs w-28" />
        <Button data-testid="soc-sla-ov-add" size="sm" variant="outline" className="rounded-sm text-xs font-mono uppercase" onClick={addOverride}>Add</Button>
      </div>

      {esc && (
        <div className="pt-3 border-t border-border space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Team Escalation (on SLA breach)</div>
            <label className="flex items-center gap-2 text-xs"><Switch data-testid="soc-esc-enabled" checked={!!esc.enabled} onCheckedChange={v => setEsc({ ...esc, enabled: v })} /> enabled</label>
          </div>
          <p className="text-xs text-muted-foreground">After a resolution breach, notify each step in turn once its delay (minutes after breach) elapses. Routing: <span className="font-mono">Assigned analyst → the case team's Team Lead → Admin/CISO</span>. If a case has no team, the Team Lead step falls back to the global SOC lead.</p>
          {(esc.steps || []).map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <Select value={s.kind || s.tier} onValueChange={v => setEsc({ ...esc, steps: esc.steps.map((x, j) => j === i ? { ...x, kind: v } : x) })}>
                <SelectTrigger data-testid={`soc-esc-step-${i}`} className="rounded-sm h-8 text-xs w-40"><SelectValue /></SelectTrigger>
                <SelectContent>{ESC_KINDS.map(t => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" value={s.after_minutes} onChange={e => setEsc({ ...esc, steps: esc.steps.map((x, j) => j === i ? { ...x, after_minutes: parseInt(e.target.value || "0", 10) } : x) })} className="rounded-sm h-8 text-xs w-24" title="minutes after breach" />
              <span className="text-[10px] text-muted-foreground">min after breach</span>
              <button onClick={() => setEsc({ ...esc, steps: esc.steps.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-rag-red"><Trash size={13} /></button>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" className="rounded-sm text-xs h-7" onClick={() => setEsc({ ...esc, steps: [...(esc.steps || []), { kind: "team_lead", after_minutes: 60 }] })}>+ step</Button>
            <Input data-testid="soc-esc-ciso" value={esc.ciso_email || ""} onChange={e => setEsc({ ...esc, ciso_email: e.target.value })} placeholder="CISO email (for CISO step)" className="rounded-sm h-8 text-xs flex-1" />
            <Button data-testid="soc-esc-save" size="sm" className="rounded-sm text-xs font-mono uppercase" onClick={saveEsc}>Save</Button>
          </div>

          <div className="mt-2 rounded-sm border border-border bg-muted/20 p-3 space-y-2" data-testid="soc-esc-preview">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Live preview</span>
              <span className="text-[11px] text-muted-foreground">for sample case</span>
              <Select value={sampleId || "none"} onValueChange={v => setSampleId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="soc-esc-preview-sample" className="rounded-sm h-8 text-xs w-72"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Unassigned, no team (fallback demo) —</SelectItem>
                  {cases.map(c => <SelectItem key={c.id} value={c.id}>{c.number} · {c.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {preview?.context && (
              <div className="text-[11px] text-muted-foreground font-mono">
                team: <span className="text-foreground">{preview.context.team_name || "—"}</span> · assignee: <span className="text-foreground">{preview.context.assignee_name || "unassigned"}</span>
              </div>
            )}
            <div className="space-y-1.5">
              {(preview?.steps || []).map((s, i) => (
                <div key={i} data-testid={`soc-esc-preview-step-${i}`} className="flex items-start gap-2 text-xs">
                  <span className="mt-0.5 h-5 w-5 shrink-0 grid place-items-center rounded-full bg-primary/15 text-primary text-[10px] font-bold font-mono">{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{s.label}</span>
                      <span className="text-[10px] font-mono text-muted-foreground">+{s.after_minutes}m</span>
                    </div>
                    {s.recipients.length > 0
                      ? <div className="text-muted-foreground">{s.recipients.map(r => r.name ? `${r.name} <${r.email}>` : r.email).join(", ")}</div>
                      : <div className="text-rag-amber-fg" data-testid={`soc-esc-preview-empty-${i}`}>No recipient resolves — notification would be skipped</div>}
                  </div>
                </div>
              ))}
              {(!preview || (preview.steps || []).length === 0) && <div className="text-xs text-muted-foreground">Add a step to preview who gets notified.</div>}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

/* ------------------ Webhook Ingest ------------------ */
function WebhookIngestCard() {
  const [cfg, setCfg] = useState(null);
  const [allow, setAllow] = useState("");
  const [oneTimeSecret, setOneTimeSecret] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/soc/ingest/config").then(r => { setCfg(r.data); setAllow((r.data.ip_allowlist || []).join("\n")); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const body = { enabled: cfg.enabled, ip_allowlist: allow.split("\n").map(s => s.trim()).filter(Boolean), ...extra };
      const { data } = await api.put("/soc/ingest/config", body);
      if (data.secret) setOneTimeSecret(data.secret);
      setCfg({ ...cfg, ...data }); load();
      toast.success("Ingest settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
  if (!cfg) return null;
  const url = `${BACKEND}/api/soc/ingest`;

  return (
    <Card className="p-5 border-border rounded-sm space-y-4" data-testid="soc-ingest-card">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2"><PlugsConnected size={14} /> SIEM / EDR Webhook Ingest</div>
        <p className="text-xs text-muted-foreground mt-1">Let external security tools POST alerts to auto-create SOC cases. Secured by a signed secret and an optional source-IP allowlist.</p>
      </div>

      <div>
        <label className="text-xs font-mono text-muted-foreground">Webhook endpoint (POST)</label>
        <div className="flex gap-2 mt-1">
          <Input data-testid="soc-ingest-url" readOnly value={url} className="rounded-sm text-xs font-mono" />
          <Button variant="outline" className="rounded-sm shrink-0" onClick={() => copy(url)} data-testid="soc-ingest-url-copy"><Copy size={15} /></Button>
        </div>
        <p className="text-[10px] text-muted-foreground font-mono mt-1">Send header <span className="text-foreground">X-SOC-Secret</span>. Body: {`{ title, severity, source, category, client, description, external_id, mitre_techniques[], iocs[] }`}</p>
      </div>

      <label className="flex items-center justify-between gap-2 text-sm border border-border rounded-sm px-3 py-2">
        <span>Enable ingest endpoint</span>
        <Switch data-testid="soc-ingest-enabled" checked={!!cfg.enabled} onCheckedChange={v => setCfg({ ...cfg, enabled: v })} />
      </label>

      <div>
        <label className="text-xs font-mono text-muted-foreground">Shared secret {cfg.secret_set && <span className="text-rag-green-fg">· configured (…{cfg.secret_hint})</span>}</label>
        <div className="flex gap-2 mt-1">
          <Button variant="outline" className="rounded-sm text-xs font-mono uppercase" onClick={() => save({ regenerate: true })} disabled={saving} data-testid="soc-ingest-regenerate"><ArrowsClockwise size={14} className="mr-1" /> {cfg.secret_set ? "Regenerate" : "Generate"} secret</Button>
        </div>
        {oneTimeSecret && (
          <div className="mt-2 p-3 rounded-sm border border-rag-amber/40 bg-rag-amber-bg" data-testid="soc-ingest-onetime">
            <div className="text-[10px] font-mono uppercase text-rag-amber-fg mb-1">Copy now — shown only once</div>
            <div className="flex gap-2 items-center">
              <code className="text-xs font-mono break-all flex-1">{oneTimeSecret}</code>
              <Button variant="outline" size="sm" className="rounded-sm shrink-0" onClick={() => copy(oneTimeSecret)}><Copy size={13} /></Button>
            </div>
          </div>
        )}
      </div>

      <div>
        <label className="text-xs font-mono text-muted-foreground">Source IP allowlist (one per line, IP or CIDR — blank = allow all)</label>
        <Textarea data-testid="soc-ingest-allowlist" value={allow} onChange={e => setAllow(e.target.value)} placeholder="203.0.113.5&#10;198.51.100.0/24" className="rounded-sm mt-1 text-sm font-mono min-h-[70px]" />
      </div>

      <div className="flex items-center gap-3">
        <Button data-testid="soc-ingest-save" onClick={() => save()} disabled={saving} className="rounded-sm font-mono uppercase text-xs">{saving ? "Saving…" : "Save ingest settings"}</Button>
        <span className="text-xs text-muted-foreground font-mono">Cases from webhook: {cfg.cases_from_webhook ?? 0}</span>
      </div>
    </Card>
  );
}

/* ------------------ Threat Intel ------------------ */
const INTEL_LINKS = {
  virustotal: "https://www.virustotal.com/gui/my-apikey",
  abuseipdb: "https://www.abuseipdb.com/account/api",
  urlscan: "https://urlscan.io/user/profile/",
  shodan: "https://account.shodan.io/",
};
function ThreatIntelCard() {
  const [cfg, setCfg] = useState(null);
  const [keys, setKeys] = useState({});
  const [th, setTh] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/soc/threatintel/config").then(r => {
    setCfg(r.data);
    const t = {};
    (r.data.connectors || []).forEach(c => { t[c.id] = { malicious: c.threshold_malicious, suspicious: c.threshold_suspicious }; });
    setTh(t);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { enabled: cfg.enabled, auto_tag: cfg.auto_tag, cache_ttl_hours: cfg.cache_ttl_hours, thresholds: th };
      Object.entries(keys).forEach(([id, v]) => { if (v && v.trim()) body[`${id}_key`] = v.trim(); });
      const { data } = await api.put("/soc/threatintel/config", body);
      load(); setKeys({}); toast.success("Threat-intel settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  if (!cfg) return null;

  return (
    <Card className="p-5 border-border rounded-sm space-y-4" data-testid="soc-intel-card">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2"><Detective size={14} /> Threat Intel Enrichment</div>
        <p className="text-xs text-muted-foreground mt-1">Pluggable connectors normalize provider results to a verdict/score. Keys are stored server-side and never returned.</p>
      </div>
      <label className="flex items-center justify-between gap-2 text-sm border border-border rounded-sm px-3 py-2">
        <span>Enable enrichment</span>
        <Switch data-testid="soc-intel-enabled" checked={!!cfg.enabled} onCheckedChange={v => setCfg({ ...cfg, enabled: v })} />
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="flex items-center justify-between gap-2 text-sm border border-border rounded-sm px-3 py-2">
          <span>Auto-tag observables by verdict</span>
          <Switch data-testid="soc-intel-autotag" checked={!!cfg.auto_tag} onCheckedChange={v => setCfg({ ...cfg, auto_tag: v })} />
        </label>
        <label className="flex items-center justify-between gap-2 text-sm border border-border rounded-sm px-3 py-2">
          <span>Cache TTL (hours)</span>
          <Input data-testid="soc-intel-ttl" type="number" min="1" value={cfg.cache_ttl_hours}
            onChange={e => setCfg({ ...cfg, cache_ttl_hours: parseInt(e.target.value || "6", 10) })}
            className="rounded-sm h-7 w-20 text-xs text-right" />
        </label>
      </div>
      <div className="space-y-3">
        {(cfg.connectors || []).map(c => (
          <div key={c.id} className="border border-border rounded-sm p-3 space-y-2" data-testid={`soc-connector-${c.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{c.name} <span className="text-[10px] font-mono text-muted-foreground">{c.types.join(", ")}</span></span>
              {c.key_set && <span className="text-[10px] font-mono text-rag-green-fg">set ({c.key_hint})</span>}
            </div>
            <Input data-testid={`soc-connector-key-${c.id}`} value={keys[c.id] || ""} onChange={e => setKeys({ ...keys, [c.id]: e.target.value })}
              placeholder={c.key_set ? "Enter new key to replace…" : `Paste ${c.name} API key`} className="rounded-sm text-sm font-mono h-8" />
            <div className="flex items-center gap-3">
              <label className="text-[10px] font-mono text-muted-foreground flex items-center gap-1">malicious ≥
                <Input data-testid={`soc-connector-mal-${c.id}`} type="number" value={(th[c.id] || {}).malicious ?? c.threshold_malicious}
                  onChange={e => setTh({ ...th, [c.id]: { ...th[c.id], malicious: parseInt(e.target.value || "0", 10) } })}
                  className="rounded-sm h-6 w-16 text-xs text-right" />
              </label>
              <label className="text-[10px] font-mono text-muted-foreground flex items-center gap-1">suspicious ≥
                <Input data-testid={`soc-connector-susp-${c.id}`} type="number" value={(th[c.id] || {}).suspicious ?? c.threshold_suspicious}
                  onChange={e => setTh({ ...th, [c.id]: { ...th[c.id], suspicious: parseInt(e.target.value || "0", 10) } })}
                  className="rounded-sm h-6 w-16 text-xs text-right" />
              </label>
              <a href={INTEL_LINKS[c.id]} target="_blank" rel="noreferrer" className="text-[10px] font-mono text-primary hover:underline ml-auto">Get key →</a>
            </div>
          </div>
        ))}
      </div>
      <Button data-testid="soc-intel-save" onClick={save} disabled={saving} className="rounded-sm font-mono uppercase text-xs">{saving ? "Saving…" : "Save threat-intel settings"}</Button>
    </Card>
  );
}

/* ------------------ Playbooks ------------------ */
const EMPTY = { name: "", category: "Other", description: "", steps: [""], auto_apply: false };
function PlaybooksCard() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/soc/playbooks").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const downloadBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };
  const exportPlaybooks = async () => {
    if (rows.length === 0) { toast.error("No playbooks to export"); return; }
    try {
      const res = await api.get("/soc/playbooks/export", { responseType: "blob" });
      downloadBlob(res.data, `soc-playbooks-${new Date().toISOString().slice(0, 10)}.json`);
      toast.success(`Exported ${rows.length} playbook(s)`);
    } catch { toast.error("Export failed"); }
  };
  const downloadTemplate = async () => {
    try {
      const res = await api.get("/soc/playbooks/template", { responseType: "blob" });
      downloadBlob(res.data, "soc-playbooks-template.json");
    } catch { toast.error("Failed to download template"); }
  };
  const onImportFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const parsed = JSON.parse(await file.text());
      const payload = Array.isArray(parsed) ? { playbooks: parsed } : parsed;
      if (!payload || !Array.isArray(payload.playbooks)) { toast.error("File must contain a 'playbooks' array"); return; }
      const { data } = await api.post("/soc/playbooks/import", payload);
      toast.success(`Imported: ${data.created} added, ${data.updated} updated${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      if (data.errors?.length) toast.error(`${data.errors.length} row(s) skipped — ${data.errors[0]}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (err instanceof SyntaxError ? "Invalid JSON file" : "Import failed"));
    } finally { setImporting(false); }
  };

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ name: p.name, category: p.category, description: p.description || "", steps: p.steps?.length ? p.steps : [""] }); setEditId(p.id); setOpen(true); };
  const setStep = (i, v) => setForm({ ...form, steps: form.steps.map((s, idx) => idx === i ? v : s) });
  const addStep = () => setForm({ ...form, steps: [...form.steps, ""] });
  const delStep = (i) => setForm({ ...form, steps: form.steps.filter((_, idx) => idx !== i) });

  const save = async () => {
    const body = { name: form.name.trim(), category: form.category, description: form.description.trim(), steps: form.steps.map(s => s.trim()).filter(Boolean), auto_apply: !!form.auto_apply };
    if (!body.name) { toast.error("Name required"); return; }
    try {
      if (editId) await api.patch(`/soc/playbooks/${editId}`, body);
      else await api.post("/soc/playbooks", body);
      setOpen(false); load(); toast.success("Playbook saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => { try { await api.delete(`/soc/playbooks/${id}`); load(); toast.success("Playbook deleted"); } catch { toast.error("Delete failed"); } };

  return (
    <Card className="p-5 border-border rounded-sm space-y-3" data-testid="soc-playbooks-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2"><ListChecks size={14} /> Response Playbooks</div>
          <p className="text-xs text-muted-foreground mt-1">Reusable checklist templates analysts apply to a case during triage.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <input ref={fileRef} type="file" accept="application/json,.json" onChange={onImportFile} className="hidden" data-testid="soc-playbook-import-input" />
          <Button data-testid="soc-playbook-template" onClick={downloadTemplate} variant="ghost" size="sm" className="rounded-sm font-mono uppercase text-xs" title="Download a sample import file"><FileText size={14} className="mr-1" /> Template</Button>
          <Button data-testid="soc-playbook-export" onClick={exportPlaybooks} variant="outline" size="sm" className="rounded-sm font-mono uppercase text-xs"><DownloadSimple size={14} className="mr-1" /> Export</Button>
          <Button data-testid="soc-playbook-import" onClick={() => fileRef.current?.click()} disabled={importing} variant="outline" size="sm" className="rounded-sm font-mono uppercase text-xs"><UploadSimple size={14} className="mr-1" /> {importing ? "Importing…" : "Import"}</Button>
          <Button data-testid="soc-playbook-new" onClick={openNew} variant="outline" size="sm" className="rounded-sm font-mono uppercase text-xs"><Plus size={14} className="mr-1" /> New</Button>
        </div>
      </div>
      <div className="space-y-2">
        {rows.map(p => (
          <div key={p.id} data-testid={`soc-playbook-${p.id}`} className="flex items-center gap-3 border border-border rounded-sm px-3 py-2">
            <span className="text-[10px] font-mono uppercase px-2 py-0.5 border border-border rounded-sm shrink-0">{p.category}</span>
            {p.auto_apply && <span data-testid={`soc-playbook-auto-${p.id}`} className="text-[10px] font-mono uppercase px-2 py-0.5 border border-rag-green/40 text-rag-green-fg rounded-sm shrink-0">Auto</span>}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{p.name}</div>
              <div className="text-xs text-muted-foreground">{p.steps?.length || 0} steps</div>
            </div>
            <button data-testid={`soc-playbook-edit-${p.id}`} onClick={() => openEdit(p)} className="text-muted-foreground hover:text-primary"><PencilSimple size={15} /></button>
            <button data-testid={`soc-playbook-del-${p.id}`} onClick={() => del(p.id)} className="text-muted-foreground hover:text-rag-red"><Trash size={15} /></button>
          </div>
        ))}
        {rows.length === 0 && <div className="text-xs text-muted-foreground">No playbooks yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="soc-playbook-dialog">
          <DialogHeader><DialogTitle className="font-mono uppercase text-sm">{editId ? "Edit" : "New"} playbook</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div>
              <label className="text-xs font-mono text-muted-foreground">Name</label>
              <Input data-testid="soc-playbook-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" />
            </div>
            <div>
              <label className="text-xs font-mono text-muted-foreground">Category</label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="soc-playbook-category" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-mono text-muted-foreground">Description</label>
              <Textarea data-testid="soc-playbook-desc" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1 min-h-[54px]" />
            </div>
            <label className="flex items-center justify-between gap-2 text-sm border border-border rounded-sm px-3 py-2">
              <span>Auto-apply to webhook cases of this category</span>
              <Switch data-testid="soc-playbook-autoapply" checked={!!form.auto_apply} onCheckedChange={v => setForm({ ...form, auto_apply: v })} />
            </label>
            <div>
              <label className="text-xs font-mono text-muted-foreground">Steps</label>
              <div className="space-y-2 mt-1">
                {form.steps.map((s, i) => (
                  <div key={i} className="flex gap-2">
                    <span className="text-xs font-mono text-muted-foreground w-5 pt-2">{i + 1}.</span>
                    <Input data-testid={`soc-playbook-step-${i}`} value={s} onChange={e => setStep(i, e.target.value)} className="rounded-sm text-sm" />
                    <button onClick={() => delStep(i)} className="text-muted-foreground hover:text-rag-red"><Trash size={14} /></button>
                  </div>
                ))}
                <Button data-testid="soc-playbook-add-step" onClick={addStep} variant="outline" size="sm" className="rounded-sm text-xs font-mono uppercase"><Plus size={13} className="mr-1" /> Add step</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm text-xs font-mono uppercase" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="soc-playbook-save" className="rounded-sm text-xs font-mono uppercase" onClick={save}>Save playbook</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ------------------ Task Templates (per case type) ------------------ */
const EMPTY_TT = { name: "", case_type: "Other", enabled: true, task_items: [{ title: "", description: "" }] };
function TaskTemplatesCard() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_TT);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/soc/task-templates").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY_TT); setEditId(null); setOpen(true); };
  const openEdit = (t) => { setForm({ name: t.name, case_type: t.case_type, enabled: t.enabled, task_items: t.task_items?.length ? t.task_items : [{ title: "", description: "" }] }); setEditId(t.id); setOpen(true); };
  const setItem = (i, k, v) => setForm({ ...form, task_items: form.task_items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) });
  const addItem = () => setForm({ ...form, task_items: [...form.task_items, { title: "", description: "" }] });
  const delItem = (i) => setForm({ ...form, task_items: form.task_items.filter((_, idx) => idx !== i) });

  const save = async () => {
    const items = form.task_items.map(it => ({ title: (it.title || "").trim(), description: (it.description || "").trim() })).filter(it => it.title);
    const body = { name: form.name.trim(), case_type: form.case_type, enabled: !!form.enabled, task_items: items };
    if (!body.name) { toast.error("Name required"); return; }
    if (items.length === 0) { toast.error("Add at least one task"); return; }
    try {
      if (editId) await api.patch(`/soc/task-templates/${editId}`, body);
      else await api.post("/soc/task-templates", body);
      setOpen(false); load(); toast.success("Task template saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const toggleEnabled = async (t) => { try { await api.patch(`/soc/task-templates/${t.id}`, { enabled: !t.enabled }); load(); } catch { toast.error("Update failed"); } };
  const del = async (id) => { try { await api.delete(`/soc/task-templates/${id}`); load(); toast.success("Template deleted"); } catch { toast.error("Delete failed"); } };

  return (
    <Card className="p-5 border-border rounded-sm space-y-3" data-testid="soc-task-templates-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2"><ListChecks size={14} /> Task Templates (per case type)</div>
          <p className="text-xs text-muted-foreground mt-1">Ordered task lists auto-added to a case when its case type matches. Separate from response playbooks.</p>
        </div>
        <Button data-testid="soc-tasktpl-new" onClick={openNew} variant="outline" size="sm" className="rounded-sm font-mono uppercase text-xs shrink-0"><Plus size={14} className="mr-1" /> New</Button>
      </div>
      <div className="space-y-2">
        {rows.map(t => (
          <div key={t.id} data-testid={`soc-tasktpl-${t.id}`} className="flex items-center gap-3 border border-border rounded-sm px-3 py-2">
            <span className="text-[10px] font-mono uppercase px-2 py-0.5 border border-border rounded-sm shrink-0">{t.case_type}</span>
            {!t.enabled && <span className="text-[10px] font-mono uppercase px-2 py-0.5 border border-border text-muted-foreground rounded-sm shrink-0">Disabled</span>}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{t.name}</div>
              <div className="text-xs text-muted-foreground">{t.task_items?.length || 0} tasks</div>
            </div>
            <Switch data-testid={`soc-tasktpl-enabled-${t.id}`} checked={!!t.enabled} onCheckedChange={() => toggleEnabled(t)} />
            <button data-testid={`soc-tasktpl-edit-${t.id}`} onClick={() => openEdit(t)} className="text-muted-foreground hover:text-primary"><PencilSimple size={15} /></button>
            <button data-testid={`soc-tasktpl-del-${t.id}`} onClick={() => del(t.id)} className="text-muted-foreground hover:text-rag-red"><Trash size={15} /></button>
          </div>
        ))}
        {rows.length === 0 && <div className="text-xs text-muted-foreground">No task templates yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="soc-tasktpl-dialog">
          <DialogHeader><DialogTitle className="font-mono uppercase text-sm">{editId ? "Edit" : "New"} task template</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div>
              <label className="text-xs font-mono text-muted-foreground">Name</label>
              <Input data-testid="soc-tasktpl-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" />
            </div>
            <div>
              <label className="text-xs font-mono text-muted-foreground">Case type</label>
              <Select value={form.case_type} onValueChange={v => setForm({ ...form, case_type: v })}>
                <SelectTrigger data-testid="soc-tasktpl-casetype" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center justify-between gap-2 text-sm border border-border rounded-sm px-3 py-2">
              <span>Enabled (auto-apply to new cases of this type)</span>
              <Switch data-testid="soc-tasktpl-enabled-toggle" checked={!!form.enabled} onCheckedChange={v => setForm({ ...form, enabled: v })} />
            </label>
            <div>
              <label className="text-xs font-mono text-muted-foreground">Tasks</label>
              <div className="space-y-2 mt-1">
                {form.task_items.map((it, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <span className="text-xs font-mono text-muted-foreground w-5 pt-2">{i + 1}.</span>
                    <div className="flex-1 space-y-1">
                      <Input data-testid={`soc-tasktpl-item-title-${i}`} value={it.title} onChange={e => setItem(i, "title", e.target.value)} placeholder="Task title" className="rounded-sm text-sm" />
                      <Input data-testid={`soc-tasktpl-item-desc-${i}`} value={it.description} onChange={e => setItem(i, "description", e.target.value)} placeholder="Description (optional)" className="rounded-sm text-xs" />
                    </div>
                    <button onClick={() => delItem(i)} className="text-muted-foreground hover:text-rag-red pt-2"><Trash size={14} /></button>
                  </div>
                ))}
                <Button data-testid="soc-tasktpl-add-item" onClick={addItem} variant="outline" size="sm" className="rounded-sm text-xs font-mono uppercase"><Plus size={13} className="mr-1" /> Add task</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm text-xs font-mono uppercase" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="soc-tasktpl-save" className="rounded-sm text-xs font-mono uppercase" onClick={save}>Save template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
